import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "https://sp-hygiene.com/api";

interface AuthState {
  user: { id: string; email: string } | null;
  token: string | null;
  refreshToken: string | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  loading: false,
  error: null,
  isAuthenticated: false,
};

// Async thunk pour se connecter
export const login = createAsyncThunk(
  "auth/login",
  async (
    credentials: { username: string; password: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, credentials);

      return response.data; // Supposons que le backend renvoie { token, refreshToken, user }
    } catch (error: any) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(error.response?.data)
      }
      return rejectWithValue("Erreur lors de la connexion");
    }
  }
);

// Async thunk pour rafraîchir le token
export const refreshAccessToken = createAsyncThunk(
  "auth/refreshAccessToken",
  async (_, { getState, rejectWithValue }) => {
    const state: any = getState();
    const refreshToken = state.auth.refreshToken;

    if (!refreshToken) {
      return rejectWithValue("Pas de refresh token disponible");
    }

    try {
      const response = await axios.post(`${API_URL}/refresh`, {}, {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      });

      return response.data;
    } catch (error: any) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(error.response?.data)
      }
      return rejectWithValue("Erreur lors du rafraîchissement du token");
    }
  }
);

// Slice d'authentification
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.user = action.payload.user;
        state.isAuthenticated = true;
      })
      .addCase(login.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Refresh token
      .addCase(
        refreshAccessToken.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.token = action.payload.token;
        }
      )
      .addCase(refreshAccessToken.rejected, (state) => {
        state.token = null;
        state.isAuthenticated = false;
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
